import { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function Interests() {
  const { t } = useLang();
  const [tab, setTab] = useState("received");
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/interests/received"), api.get("/interests/sent")]);
      setReceived(r.data); setSent(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/interests/${id}/action`, { action });
      toast.success(action === "accept" ? "It's a match!" : "Declined");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const items = tab === "received" ? received : sent;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="interests-page">
      <p className="overline mb-3">{t("interests.kicker")}</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">{t("interests.title")}</h1>

      <div className="mt-10 flex gap-1 p-1 rounded-full bg-[#F2EFE9] w-fit">
        {["received", "sent"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-5 py-2 rounded-full text-sm capitalize transition ${tab === k ? "bg-white shadow-sm" : "text-[#8C857B]"}`}>
            {k === "received" ? t("interests.tabReceived") : t("interests.tabSent")} {k === "received" ? `(${received.length})` : `(${sent.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-32 text-[#8C857B]">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-24 mt-8 border border-dashed border-[#E2DCD0] rounded-2xl text-[#8C857B]" data-testid="interests-empty">
          {t("interests.empty")}
        </div>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => {
            const u = it.user; if (!u) return null;
            const cover = u.photos?.[0] ? fileUrl(u.photos[0]) : null;
            return (
              <div key={it.id} className="p-5 rounded-2xl border border-[#E2DCD0] bg-white" data-testid={`interest-item-${it.id}`}>
                <div className="flex items-center gap-4">
                  <Link to={`/profile/${u.id}`} className="relative w-16 h-16 rounded-xl overflow-hidden bg-[#F2EFE9] flex-shrink-0">
                    {cover ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
                      <div className="absolute inset-0 flex items-center justify-center font-serif-display text-2xl text-[#8C857B]">{u.full_name?.[0]}</div>}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${u.id}`} className="font-serif-display text-xl block truncate hover:text-[#C85A44]">{u.full_name}</Link>
                    <div className="text-xs text-[#8C857B] truncate">{u.age ? `${u.age} yrs` : ""}{u.city ? ` · ${u.city}` : ""}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`text-xs uppercase tracking-widest ${
                    it.status === "accepted" ? "text-[#2E5C46]" :
                    it.status === "rejected" ? "text-[#9C3A3A]" : "text-[#8C857B]"
                  }`}>{it.status}</span>
                  {tab === "received" && it.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => act(it.id, "accept")} data-testid={`accept-${it.id}`}
                        className="rounded-full bg-[#2E5C46] hover:opacity-90 text-white px-3 py-1.5 text-xs inline-flex items-center gap-1">
                        <Check className="h-3 w-3"/> {t("interests.accept")}
                      </button>
                      <button onClick={() => act(it.id, "reject")} data-testid={`reject-${it.id}`}
                        className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-3 py-1.5 text-xs inline-flex items-center gap-1">
                        <X className="h-3 w-3"/> {t("interests.decline")}
                      </button>
                    </div>
                  )}
                  {it.status === "accepted" && (
                    <Link to={`/chat/${u.id}`} data-testid={`chat-link-${it.id}`} className="text-xs text-[#C85A44] hover:underline">Open chat →</Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
